// Vehicle registration lookup - invokes the existing Firebase Cloud Function
// `lookupVehicle` (Callable, region europe-west2). The function proxies
// DVLA + MOT History APIs with server-side credentials.

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import type { VehicleLookupResponse } from '../types/models';
import { Alert } from '../components/ui/Alert';
import { PageHeader } from '../components/ui/Page';
import { RegPlate } from '../components/ui/RegPlate';
import { usePageTitle } from '../hooks/usePageTitle';

export default function VehicleLookupPage() {
  usePageTitle('Reg check');
  const [reg, setReg] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<VehicleLookupResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e?: React.FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    const cleaned = reg.trim().toUpperCase();
    if (cleaned.length < 2) return;
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const call = httpsCallable<
        { registration: string },
        VehicleLookupResponse
      >(functions, 'lookupVehicle');
      const res = await call({ registration: cleaned });
      setData(res.data);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Lookup failed';
      setError(
        msg.toLowerCase().includes('app check') ||
          msg.toLowerCase().includes('appcheck') ||
          msg.toLowerCase().includes('unauthenticated')
          ? 'Vehicle lookup is blocked because Firebase App Check is not configured for this web app.'
          : msg
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Reg check"
        description="Look a registration up against DVLA and the MOT history. Nothing here creates a job."
      />

      <form onSubmit={onSubmit} className="mt-5 overflow-hidden rounded-[14px] border border-hair bg-elev p-5">
        <div className="eyebrow mb-2.5">Registration</div>
        <RegPlate value={reg} onChange={setReg} onSearch={() => onSubmit()} searching={loading} autoFocus />
        <p className="mt-2.5 text-[13px] text-ink3">Spaces optional. Return looks it up.</p>
      </form>

      {error && <div className="mt-4"><Alert tone="error">{error}</Alert></div>}

      {data && <VehicleResult data={data} />}
    </div>
  );
}

// One answer per field (Bobby, 14 Oct 2026). The page used to print the whole
// DVLA record and then the MOT service's make, model, fuel and colour again.
// DVLA has no model, so Model comes from the MOT service; everything else
// prefers DVLA and falls back to MOT. Dates are UK (DD/MM/YYYY).
function VehicleResult({ data }: { data: VehicleLookupResponse }) {
  const dvla = data.dvla ?? {};
  const mot = data.motVehicleInfo;
  const hasDvla = Object.keys(dvla).length > 0;
  const field = (key: string) => {
    const value = dvla[key];
    return value === null || value === undefined || value === '' ? null : value;
  };

  const make = text(field('make')) ?? mot?.make ?? null;
  const fuel = text(field('fuelType')) ?? mot?.fuelType ?? null;
  const colour = text(field('colour')) ?? mot?.primaryColour ?? null;
  const revenueWeight = field('revenueWeight');
  const engineCapacity = field('engineCapacity');
  const co2 = field('co2Emissions');

  return (
    <div className="mt-5 grid items-start gap-5 lg:grid-cols-2">
      <section className="overflow-hidden rounded-[14px] border border-hair bg-elev">
        <h2 className="px-5 pt-4 text-[16.5px] font-semibold text-ink">
          Vehicle <span className="font-mono text-[15px] font-medium text-ink3">{data.registration}</span>
        </h2>
        {!hasDvla && !mot ? (
          <p className="px-5 pb-4 pt-2 text-[14px] text-ink3">No vehicle record returned.</p>
        ) : (
          <>
            <div className="px-5 pb-2 pt-2">
              <Row label="Make" value={make ?? '—'} />
              <Row label="Model" value={mot?.model ?? '—'} />
              <Row label="Fuel" value={fuel ?? '—'} />
              <Row label="Colour" value={colour ?? '—'} />
              <Row label="Tax status" value={text(field('taxStatus')) ?? '—'} />
              <Row label="Tax due" value={ukDate(field('taxDueDate')) ?? '—'} mono />
              <Row label="Year of manufacture" value={text(field('yearOfManufacture')) ?? '—'} mono />
              <Row
                label="Revenue weight"
                value={revenueWeight === null ? 'Not held by DVLA' : `${text(revenueWeight)} kg`}
                mono={revenueWeight !== null}
                muted={revenueWeight === null}
              />
            </div>
            <div className="border-t border-hair px-5 pb-2 pt-3">
              <div className="eyebrow mb-1">Also from DVLA</div>
              <Row label="V5C last issued" value={ukDate(field('dateOfLastV5CIssued')) ?? '—'} mono small />
              <Row label="Engine capacity" value={engineCapacity === null ? '—' : `${text(engineCapacity)} cc`} mono small />
              <Row label="CO₂ emissions" value={co2 === null ? '—' : `${text(co2)} g/km`} mono small />
            </div>
            {!hasDvla && (
              <p className="px-5 pb-4 text-[13px] text-ink3">No DVLA record returned; these details are from the MOT history.</p>
            )}
          </>
        )}
      </section>

      <section className="overflow-hidden rounded-[14px] border border-hair bg-elev">
        <h2 className="px-5 pt-4 text-[16.5px] font-semibold text-ink">MOT history</h2>
        <div className="px-5 pb-5 pt-2">
          {data.mot.length === 0 ? (
            <p className="py-1 text-[14px] text-ink3">No MOT tests on record.</p>
          ) : (
            <>
              <div className="eyebrow mb-2">{data.mot.length} test{data.mot.length === 1 ? '' : 's'}</div>
              <ol className="max-h-80 space-y-2 overflow-auto">
                {data.mot.map((entry, index) => {
                  const test = entry as Record<string, unknown>;
                  const passed = String(test.testResult ?? '').toUpperCase().includes('PASS');
                  return (
                    <li key={index} className="rounded-[10px] bg-surface px-3.5 py-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className={`text-[14px] font-semibold ${passed ? 'text-success-ink' : 'text-st-cancelled'}`}>
                          {String(test.testResult ?? 'Unknown')}
                        </span>
                        <span className="font-mono text-[13px] text-ink3">{ukDate(test.completedDate) ?? ''}</span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-4 text-[13px] text-ink2">
                        <span>Mileage <span className="font-mono">{String(test.odometerValue ?? '—')}</span> {String(test.odometerUnit ?? '')}</span>
                        <span>Expires <span className="font-mono">{ukDate(test.expiryDate) ?? '—'}</span></span>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function text(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  return s === '' ? null : s;
}

// DVLA sends dates as 2027-04-01 (or 2024-11 for a month); the MOT service as
// 2023-05-10T12:34:56.000Z or 2023.05.10. Shown as 01/04/2027 and 11/2024.
// Anything else is shown as it came.
function ukDate(value: unknown): string | null {
  const s = text(value);
  if (!s) return null;
  const full = /^(\d{4})[-.](\d{2})[-.](\d{2})/.exec(s);
  if (full) return `${full[3]}/${full[2]}/${full[1]}`;
  const month = /^(\d{4})-(\d{2})$/.exec(s);
  if (month) return `${month[2]}/${month[1]}`;
  return s;
}

function Row({ label, value, mono = false, muted = false, small = false }: {
  label: string;
  value: string;
  mono?: boolean;
  muted?: boolean;
  small?: boolean;
}) {
  return (
    <div className={`flex flex-col gap-0.5 border-t border-rowhair py-2.5 first:border-t-0 sm:flex-row sm:items-center sm:gap-3 ${small ? 'min-h-10' : 'min-h-12'}`}>
      <span className={`${small ? 'text-[13.5px]' : 'text-[14.5px]'} text-ink2 sm:shrink-0`}>{label}</span>
      <span
        className={`min-w-0 flex-1 break-words sm:text-right ${small ? 'text-[14px]' : 'text-[15px]'} ${muted ? 'text-ink3' : 'text-ink'} ${mono ? 'font-mono' : ''}`}
      >
        {value}
      </span>
    </div>
  );
}
